import { type CSSObject, type Theme } from "@mui/material/styles";

/**
 * The geometry every outlined field shares — padding, type step, the icons at either end and the space
 * between them and the value — so `ui/text-field`, `ui/select` and `ui/date-picker` inherit one set of
 * numbers instead of restating them. Keyed on MUI's own size names, which are also Apollo's `FieldSize`:
 * a field passes its size straight through to MUI, and the theme's size variants do the rest.
 */
export type FieldControlSize = "medium" | "small";

/**
 * Figma's `input/{size}/container` box: 24px line + 8px padding = 40px medium, 20px + 6px = 32px
 * small. The border costs no height — MUI's notched outline is an absolutely positioned `fieldset`.
 */
export const CONTROL_PADDING: Record<FieldControlSize, { x: number; y: number }> = {
  medium: { x: 2, y: 1 },
  small: { x: 1.5, y: 0.75 },
};

/** The height that padding produces, for anything that has to hold a field's place before it renders. */
export const CONTROL_HEIGHT: Record<FieldControlSize, number> = { medium: 40, small: 32 };

/** The type step the value, the placeholder and any text in the control are set in. */
export const FIELD_TYPOGRAPHY = {
  medium: "body1",
  small: "body2",
} as const satisfies Record<FieldControlSize, "body1" | "body2">;

/** Figma's `input/{size}/container/spacing`: between an icon and the value, and between two icons. */
export const ACTIONS_GAP: Record<FieldControlSize, number> = { medium: 1.25, small: 1 };

/**
 * Figma's `input/{size}/container/icon`, for a field's start and end icons. The file binds 20 at both
 * sizes; the Medium (`small`) field takes 16, the same step as its clear icon.
 */
export const CONTROL_ICON_SIZE: Record<FieldControlSize, number> = { medium: 20, small: 16 };

/** Figma's `input/{size}/container/clear-icon` — the glyph of every icon button inside a field. */
export const CLEAR_ICON_SIZE: Record<FieldControlSize, number> = { medium: 20, small: 16 };

/**
 * The hover and press circle of every icon button inside a field — the clear icons, `ui/select`'s
 * chevron, `ui/date-picker`'s calendar — one diameter per size whatever the glyph, 8px short of the
 * control so it clears the border top and bottom. Not a Figma value: Figma draws no hover for these.
 */
export const FIELD_ICON_BUTTON_SIZE: Record<FieldControlSize, number> = { medium: 32, small: 24 };

/**
 * Pads a glyph out to that circle and cancels the padding with an equal negative margin, so it costs no
 * layout: the glyph sits exactly where Figma draws it while the circle spills into the space around it.
 */
export const fieldIconButtonSx = (size: FieldControlSize, glyphSize: number) => {
  const inset = (FIELD_ICON_BUTTON_SIZE[size] - glyphSize) / 2;

  return { padding: `${inset}px`, margin: `-${inset}px` };
};

/**
 * The wrapper a field puts round each start or end icon, which the geometry sizes and colours. A class of
 * its own rather than the adornment's children: a start `InputAdornment` renders a zero-width
 * `.notranslate` span ahead of them, which a size on the children would stretch to the icon's. Only an
 * `svg` inside it is sized, so a non-glyph — `ui/select`'s badge `endIcon`, a colour swatch — keeps its
 * own. Prefixed `ApolloInput-` so `generateUtilityClass` can never mint the same name.
 */
export const INPUT_ICON_CLASS = "ApolloInput-icon";

/**
 * Everything that does not change with size. The icon colours are Figma's — `text.primary` before the
 * value, `text.secondary` after it — inherited by any `currentColor` glyph; a colour on the glyph itself
 * still wins. Figma keeps both when the field is disabled. `maxHeight: none` releases MUI's `2em` cap,
 * which would clip an adornment taller than the value line.
 */
export const outlinedInputBase = (theme: Theme): CSSObject => ({
  "& .MuiInputAdornment-root": { maxHeight: "none" },
  [`& .${INPUT_ICON_CLASS}`]: { display: "flex", alignItems: "center", flexShrink: 0 },
  [`& .MuiInputAdornment-positionStart .${INPUT_ICON_CLASS}`]: { color: theme.palette.text.primary },
  [`& .MuiInputAdornment-positionEnd .${INPUT_ICON_CLASS}`]: { color: theme.palette.text.secondary },
});

/**
 * The icons and the icon buttons a field's adornments hold, and the space between them and the value —
 * the same in every field, whichever control MUI draws it with.
 */
const adornmentSizeStyle = (theme: Theme, size: FieldControlSize): CSSObject => {
  const gap = theme.spacing(ACTIONS_GAP[size]);
  const icon = CONTROL_ICON_SIZE[size];
  const glyph = CLEAR_ICON_SIZE[size];

  return {
    "& .MuiInputAdornment-positionStart": { marginRight: gap },
    // Not `gap`: the start adornment's first child is MUI's zero-width `.notranslate` spacer, which a
    // `gap` would push the first icon away from by a whole step.
    [`& .MuiInputAdornment-positionStart .${INPUT_ICON_CLASS} + .${INPUT_ICON_CLASS}`]: { marginLeft: gap },
    "& .MuiInputAdornment-positionEnd": { marginLeft: gap, gap },
    [`& .${INPUT_ICON_CLASS} > svg`]: { width: icon, height: icon },
    "& .MuiInputAdornment-root .MuiIconButton-root": fieldIconButtonSx(size, glyph),
    "& .MuiInputAdornment-root .MuiIconButton-root > svg": { width: glyph, height: glyph },
  };
};

/**
 * The per-size geometry. Each rule is written from the root, so it carries a class more than MUI's own
 * rule for the same element — the input's padding, the adornment's 8px margin, the root's 14px
 * `adornedStart` / `adornedEnd` padding — and wins whatever the order.
 *
 * Per side, never the `padding` shorthand, so an adornment resets exactly the side it sits on. The inset
 * moves from the input to the root once an adornment sits on that side; `multiline` pads the root
 * instead and zeroes the input. It is written ahead of the adornment rules, which have to win on the
 * right when a multiline field carries one.
 */
export const outlinedInputSizeStyle = (theme: Theme, size: FieldControlSize): CSSObject => {
  const x = theme.spacing(CONTROL_PADDING[size].x);
  const y = theme.spacing(CONTROL_PADDING[size].y);

  return {
    ...theme.typography[FIELD_TYPOGRAPHY[size]],
    /**
     * `height: auto` is what lets the type step size the box. MUI's `InputBase` input is
     * `box-sizing: content-box` with an explicit `height: 1.4375em`, so without this the control
     * measures 23px + padding whatever the line-height says.
     */
    "& .MuiOutlinedInput-input": {
      paddingTop: y,
      paddingBottom: y,
      paddingLeft: x,
      paddingRight: x,
      height: "auto",
    },
    "&.MuiInputBase-multiline": {
      paddingTop: y,
      paddingBottom: y,
      paddingLeft: x,
      paddingRight: x,
      "& .MuiOutlinedInput-input": { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 },
    },
    "&.MuiInputBase-adornedStart": {
      paddingLeft: x,
      "& .MuiOutlinedInput-input": { paddingLeft: 0 },
    },
    "&.MuiInputBase-adornedEnd": {
      paddingRight: x,
      "& .MuiOutlinedInput-input": { paddingRight: 0 },
    },
    ...adornmentSizeStyle(theme, size),
  };
};

/**
 * MUI X draws `ui/date-picker`'s field with its own `PickersOutlinedInput`, which pads its root `0 14px`
 * whatever sits inside it and its sections container `16.5px 0` / `8.5px 0`. The same numbers as an
 * `OutlinedInput`, restated on its parts; the adornments are shared outright.
 */
export const pickersInputSizeStyle = (theme: Theme, size: FieldControlSize): CSSObject => ({
  ...theme.typography[FIELD_TYPOGRAPHY[size]],
  paddingLeft: theme.spacing(CONTROL_PADDING[size].x),
  paddingRight: theme.spacing(CONTROL_PADDING[size].x),
  "& .MuiPickersInputBase-sectionsContainer": {
    paddingTop: theme.spacing(CONTROL_PADDING[size].y),
    paddingBottom: theme.spacing(CONTROL_PADDING[size].y),
  },
  ...adornmentSizeStyle(theme, size),
});

/** The sections are inline spans; inheriting the type step's line-height is what sizes the box. */
export const pickersInputBase = (theme: Theme): CSSObject => ({
  ...outlinedInputBase(theme),
  "& .MuiPickersSectionList-root, & .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent":
    { lineHeight: "inherit" },
});
